import { ShareIcon, HeartIcon, EyeIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';

const StreamInfo = ({
  stream,
  user,
  handleShare,
  viewerCount,
  isStreamCreator,
  playbackError,
  endingStream,
  handleEndStream
}) => {
  const formatAddress = (address) => {
    if (!address) return 'Anonymous';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 p-6 border-b border-gray-700/50">
        <div className="flex items-start gap-4">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="relative"
          >
            <img
              src={stream.creator?.avatar || `https://placehold.co/64x64/8B5CF6/ffffff?text=${stream.creator?.address?.charAt(2)}`}
              alt={formatAddress(stream.creator?.address)}
              className="w-16 h-16 rounded-full border-2 border-purple-500/50"
            />
            {stream.status === 'live' && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-red-500 rounded-full border-2 border-gray-800 flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              </div>
            )}
          </motion.div>

          <div className="flex-1">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2 leading-tight">{stream.title}</h1>
                <div className="flex items-center gap-2 mb-3">
                  <p className="text-purple-400 hover:text-purple-300 cursor-pointer font-medium">
                    {stream.creator?.username || formatAddress(stream.creator?.address)}
                  </p>
                  {stream.creator?.verified && (
                    <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Stream stats */}
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1 text-red-400">
                    <EyeIcon className="w-4 h-4" />
                    <span className="font-medium">{viewerCount}</span>
                    <span className="text-gray-400">{viewerCount === 1 ? 'viewer' : 'viewers'}</span>
                  </div>
                  {stream.category && (
                    <div className="flex items-center gap-1 text-gray-400">
                      <span>•</span>
                      <span>{stream.category}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleShare}
                  className="p-3 bg-gray-700/50 hover:bg-gray-600/50 rounded-xl transition-all duration-200 backdrop-blur-sm border border-gray-600/50"
                  title="Share stream"
                >
                  <ShareIcon className="h-5 w-5 text-white" />
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content section */}
      <div className="p-6">
        {stream.description && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-400 mb-2">About this stream</h3>
            <p className="text-gray-300 leading-relaxed">{stream.description}</p>
          </div>
        )}

        {/* Stream creator controls */}
        {isStreamCreator && stream.status === 'live' && (
          <div className="mt-6">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                if (window.confirm('Are you sure you want to end the stream?')) {
                  handleEndStream();
                }
              }}
              disabled={endingStream}
              className={`px-6 py-3 ${endingStream ? 'bg-red-400' : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800'} text-white rounded-xl transition-all duration-200 flex items-center gap-3 font-medium shadow-lg hover:shadow-red-500/25`}
            >
              {endingStream && (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              )}
              {endingStream ? 'Ending Stream...' : 'End Stream'}
            </motion.button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StreamInfo;
